'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useImageZoom } from '../documents/hooks/useImageZoom';
import { PartNameCombobox } from '../shared/PartNameCombobox';
import { fetchWithAuthRetry } from '@/lib/auth/fetch-with-auth-retry';

const PdfViewer = dynamic(
  () => import('./PdfViewer').then(m => m.PdfViewer),
  {
    ssr: false,
    loading: () => (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        <p className="text-sm text-gray-400">Loading PDF viewer…</p>
      </div>
    ),
  },
);

const API_URL    = process.env.NEXT_PUBLIC_API_URL        || 'http://localhost:8000';
const AI_SVC_URL = process.env.NEXT_PUBLIC_AI_SERVICE_URL || 'http://localhost:8103';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DocRecord {
  id: string;
  doc_type: string;
  file_name: string | null;
  file_size: number | null;
  content_type: string | null;
  uploaded_at: string;
  download_url: string;
}

interface LineItem {
  description: string;
  // Canonical catalog name selected from the Part Name dropdown -- kept
  // separate from `description` so picking a match doesn't overwrite the
  // free-text description that came from the document/OCR.
  part_name: string;
  part_number: string;
  quantity: string;
  unit_price: string;
  tax: string;
  amount: string;
  // Derived, not user-set -- 'labour' iff part_type is the "Labour" entry
  // in vehicle_schema.vehicle_part_types (see isLabourPartType below). Kept
  // only because sync_estimate_line_items()/save validation still branch on
  // it; the Type column itself is now just the Part Type dropdown.
  item_type: 'part' | 'labour' | '';
  // Repair/Replace choice for this part — mirrors the Assessment tab's own
  // dropdown (PartDetailPanel.tsx) and syncs into
  // ClaimPartAssessment.repair_type via sync_estimate_line_items().
  repair_type: 'Repair' | 'Replace' | '';
  // Material/part category (e.g. MetalicParts, FiberGlassParts, or the
  // "Labour" entry) — shown in the Type column, sourced from
  // vehicle_schema.vehicle_part_types (same table feeding the Assessment
  // tab's own "Part category" dropdown) and synced into
  // ClaimPartAssessment.part_type via sync_estimate_line_items().
  part_type: string;
  // "Tax Included?" checkbox state -- frontend-only (not part of the saved
  // extraction payload, same as every other UI-only field here isn't listed
  // in ExtractionPayload on the backend: extra fields are silently dropped
  // on save). Amount is the authoritative tax-inclusive total as extracted
  // and is NEVER modified by this checkbox. Checking it splits the CURRENT
  // Amount into Price (unit_price) + Tax via splitTaxInclusiveAmount, so
  // Price + Tax == Amount; unchecking just clears Tax back to 0.
  tax_included?: boolean;
  // "Tax Applicable?" checkbox -- UNLIKE tax_included above, this IS part
  // of the saved extraction payload (ExtractionPayload.LineItem.tax_applicable
  // on the backend): synced into ClaimPartAssessment.tax_applicable by
  // sync_bill_line_items() for an invoice, or applied via the Assessment
  // tab's Map Work Order Item dropdown for a work order/estimate. Gates
  // whether Tax %/Tax Amount are editable there.
  tax_applicable: boolean;
}

// The Type column's dropdown IS the Part Type lookup -- a row is "labour"
// iff its selected Part Type is the "Labour" entry a super-admin adds under
// Super Admin > Lookups > Vehicle Part Types (matched by name, case-
// insensitive, since that's the only stable-enough handle exposed to the
// frontend by the /part-types/lookup endpoint).
function isLabourPartType(partType: string): boolean {
  return partType.trim().toLowerCase() === 'labour';
}

interface ExtractionFields {
  // Document identification
  estimate_no: string;
  estimate_date: string;
  invoice_no: string;
  invoice_date: string;
  document_type: string;
  // Vendor / Garage
  vendor_name: string;
  vendor_address: string;
  vendor_gst: string;
  // Vehicle details
  vehicle_reg_no: string;
  vin_number: string;
  engine_number: string;
  vehicle_model: string;
  insurance_company: string;
  // Amounts
  parts_cost: string;
  labor_charges: string;
  tax_amount: string;
  discount_amount: string;
  total_amount: string;
  // Line items
  line_items: LineItem[];
}

const EMPTY_FIELDS: ExtractionFields = {
  estimate_no: '', estimate_date: '',
  invoice_no: '', invoice_date: '',
  document_type: '',
  vendor_name: '', vendor_address: '', vendor_gst: '',
  vehicle_reg_no: '', vin_number: '', engine_number: '',
  vehicle_model: '', insurance_company: '',
  parts_cost: '', labor_charges: '', tax_amount: '',
  discount_amount: '', total_amount: '',
  line_items: [],
};

const EMPTY_LINE: LineItem = {
  description: '', part_name: '', part_number: '', quantity: '1',
  unit_price: '0', tax: '0', amount: '0', item_type: 'part',
  repair_type: '', part_type: '', tax_included: false, tax_applicable: true,
};

// GST rate this checkbox assumes (CGST 9% + SGST 9%, the standard rate on
// these invoices/work orders -- matches every example seen in practice).
// Price is floored (not rounded) to 2dp so Tax, computed as the remainder,
// always makes Price + Tax sum back to exactly the original Amount with no
// rounding drift -- standard invoice-splitting convention.
const TAX_INCLUSIVE_RATE = 1.18;

// totalAmount is the row's Amount -- the tax-inclusive figure, left
// untouched by the caller. Returns the pre-tax Price and the Tax portion;
// Price + Tax always equals totalAmount back exactly.
function splitTaxInclusiveAmount(totalAmount: number): { price: string; tax: string } {
  const price = Math.floor((totalAmount / TAX_INCLUSIVE_RATE) * 100) / 100;
  const tax = totalAmount - price;
  return { price: price.toFixed(2), tax: tax.toFixed(2) };
}

// ── Props ─────────────────────────────────────────────────────────────────────

// A sibling estimate/invoice for the same claim, paired with its display
// label, used to populate the segmented switcher (design Section 6.5).
export interface SiblingDoc {
  doc: DocRecord;
  docLabel: string;
}

interface Props {
  doc: DocRecord;
  docLabel: string;
  jobId: string;
  onClose: () => void;
  // Other estimates/invoices for this claim (including this one). When
  // there are 2 or more entries, a segmented Estimate/Invoice switcher
  // renders and lets the adjuster switch the modal's active doc in place
  // without closing/reopening. Omitted or a single entry -> no switcher,
  // preserving today's one-doc-per-modal-open behavior exactly.
  siblingDocs?: SiblingDoc[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token    = localStorage.getItem('access_token') || localStorage.getItem('super_admin_token') || '';
  const tenantId = localStorage.getItem('tenant_id') || '';
  const userType = localStorage.getItem('user_type') || '';
  return { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId, 'X-User-Type': userType };
}

const isInvoiceType  = (dt: string) => dt.startsWith('repair_invoice');
const isEstimateType = (dt: string) => dt.startsWith('estimate');

// 'repair_invoice_1' -> 1, 'repair_invoice_2' -> 2, etc. Null for anything
// that isn't one of the 3 numbered invoice slots (e.g. estimate docs).
function invoiceSlotNumber(docType: string): number | null {
  const m = /^repair_invoice_(\d+)$/.exec(docType);
  return m ? parseInt(m[1], 10) : null;
}

function nvl(v: unknown): string {
  if (v == null || v === 'null' || v === 'None' || v === 'undefined') return '';
  const s = String(v).trim();
  return s === 'null' || s === 'None' ? '' : s;
}

function numStr(v: unknown): string {
  if (v == null) return '';
  const n = Number(v);
  return isNaN(n) || n === 0 ? (String(v).trim() === '0' ? '0' : '') : String(n);
}

// Mirrors the Assessment tab's REPAIR_TYPES casing (assessmentTypes.tsx) —
// 'Repair'/'Replace', not lowercase — since this value round-trips through
// ClaimPartAssessment.repair_type via sync_estimate_line_items().
function normalizeRepairType(v: unknown): 'Repair' | 'Replace' | '' {
  const s = nvl(v).toLowerCase();
  if (s === 'repair') return 'Repair';
  if (s === 'replace') return 'Replace';
  return '';
}

/** Map Claude's response JSON to our ExtractionFields shape. */
function mapClaudeToFields(f: Record<string, unknown>): Partial<ExtractionFields> {
  const rawItems = Array.isArray(f.line_items) ? f.line_items as Record<string, unknown>[] : [];

  return {
    estimate_no:       nvl(f.estimate_number),
    estimate_date:     nvl(f.estimate_date),
    invoice_no:        nvl(f.invoice_number),
    invoice_date:      nvl(f.invoice_date),
    document_type:     nvl(f.document_type),
    vendor_name:       nvl(f.vendor_name),
    vendor_address:    nvl(f.vendor_address),
    vendor_gst:        nvl(f.gst_number),
    vehicle_reg_no:    nvl(f.vehicle_registration_number),
    vin_number:        nvl(f.vin_number),
    engine_number:     nvl(f.engine_number),
    vehicle_model:     nvl(f.vehicle_model),
    insurance_company: nvl(f.insurance_company),
    parts_cost:        numStr(f.parts_cost),
    labor_charges:     numStr(f.labour_cost),
    tax_amount:        numStr(f.tax_amount),
    discount_amount:   numStr(f.discount_amount),
    total_amount:      numStr(f.total_amount),
    line_items: rawItems.map(item => {
      const part_type = nvl(item.part_type);
      // Derive from part_type first (current source of truth); fall back to
      // a legacy saved item_type for rows saved before the Type column
      // became the Part Type dropdown.
      const item_type: 'part' | 'labour' = part_type
        ? (isLabourPartType(part_type) ? 'labour' : 'part')
        : (item.item_type === 'labour' ? 'labour' : 'part');
      return {
        description: nvl(item.description),
        part_name:   nvl(item.part_name),
        part_number: nvl(item.part_number),
        quantity:    numStr(item.quantity) || '1',
        unit_price:  numStr(item.unit_price) || '0',
        tax:         numStr(item.tax) || '0',
        amount:      numStr(item.amount) || '0',
        item_type,
        repair_type: normalizeRepairType(item.repair_type),
        part_type,
        // Documents saved before this checkbox existed have no key here at
        // all -- default true so they read the same as today (tax
        // editable in Assessment) until someone explicitly unchecks it.
        tax_applicable: typeof item.tax_applicable === 'boolean' ? item.tax_applicable : true,
      };
    }),
  };
}

/** Validate parts + labour + tax − discount ≈ total (2% tolerance). */
// Splits every line item's Amount into 4 buckets by (Parts vs Labour) x
// (Tax Incl.? vs not), mirroring the Tax Incl.?/Tax Appl.? columns already
// on each row. Parts Total (Tax Incl.) + Parts Total (Tax Not Incl.) +
// Labour Total (Tax Incl.) + Labour Total (Tax Not Incl.) is just the sum
// of every row's Amount column re-grouped for display. This is the pre-tax
// line-items subtotal -- on invoices that state GST as one invoice-level
// line (not baked into each row), that subtotal does NOT by itself equal
// Total Amount; the top-level Tax/GST still needs adding (and Discount
// subtracting) to reconcile, same as amountWarning() below does against
// the independently-typed Parts Cost/Labour Charges fields.
interface BillBreakdown {
  partsTaxIncl: number;
  partsTaxNotIncl: number;
  labourTaxIncl: number;
  labourTaxNotIncl: number;
  computedTotal: number;
}

function computeBillBreakdown(items: LineItem[]): BillBreakdown {
  let partsTaxIncl = 0, partsTaxNotIncl = 0, labourTaxIncl = 0, labourTaxNotIncl = 0;
  for (const item of items) {
    const amount = parseFloat(item.amount) || 0;
    const isLabour = item.item_type === 'labour';
    if (isLabour) {
      if (item.tax_included) labourTaxIncl += amount; else labourTaxNotIncl += amount;
    } else {
      if (item.tax_included) partsTaxIncl += amount; else partsTaxNotIncl += amount;
    }
  }
  return {
    partsTaxIncl, partsTaxNotIncl, labourTaxIncl, labourTaxNotIncl,
    computedTotal: partsTaxIncl + partsTaxNotIncl + labourTaxIncl + labourTaxNotIncl,
  };
}

// Grand total shown/validated against Total Amount: the 4-bucket line-items
// subtotal, plus the invoice-level Tax/GST field, minus Discount -- mirrors
// amountWarning()'s formula but sources the Parts/Labour figure from the
// actual line items instead of the separately-typed summary fields.
function computeGrandBillTotal(f: ExtractionFields): number {
  const { computedTotal } = computeBillBreakdown(f.line_items);
  const tax = parseFloat(f.tax_amount) || 0;
  const discount = parseFloat(f.discount_amount) || 0;
  return computedTotal + tax - discount;
}

// Pure arithmetic cross-check (the 4 buckets are re-grouped from the SAME
// line items Total Amount is meant to summarize, plus Tax/GST minus
// Discount) so the tolerance only needs to absorb 2-decimal rounding, not
// independent-source OCR noise -- unlike amountWarning() below, which
// compares against separately-typed Parts Cost/Labour Charges/Tax/Discount
// fields and uses a looser 2% relative tolerance for that reason.
const BILL_TOTAL_TOLERANCE = 0.5;

function billTotalMismatch(f: ExtractionFields): string | null {
  if (f.line_items.length === 0) return null;
  const total = parseFloat(f.total_amount);
  if (isNaN(total)) return null;
  const grandTotal = computeGrandBillTotal(f);
  if (Math.abs(grandTotal - total) > BILL_TOTAL_TOLERANCE) {
    return `Line items + Tax/GST − Discount sum to ₹${grandTotal.toFixed(2)}, but Total Amount is ₹${total.toFixed(2)}. Fix the line items, Tax/GST, Discount, or Total Amount before saving.`;
  }
  return null;
}

function amountWarning(f: ExtractionFields): string | null {
  const total = parseFloat(f.total_amount) || 0;
  if (!total) return null;
  const parts    = parseFloat(f.parts_cost)      || 0;
  const labour   = parseFloat(f.labor_charges)   || 0;
  const tax      = parseFloat(f.tax_amount)      || 0;
  const discount = parseFloat(f.discount_amount) || 0;
  const computed = parts + labour + tax - discount;
  if (Math.abs(computed - total) / total > 0.02) {
    return `Amount mismatch: Parts ₹${parts} + Labour ₹${labour} + Tax ₹${tax} − Discount ₹${discount} = ₹${computed.toFixed(2)}, but Total is ₹${total.toFixed(2)}`;
  }
  return null;
}

// ── Confidence badge ──────────────────────────────────────────────────────────

function ConfidenceBadge({ score }: { score: number; label: string }) {
  const cfg =
    score >= 95 ? { bg: 'bg-green-50 border-green-200',  text: 'text-green-700',  dot: 'bg-green-500',  title: 'Excellent' } :
    score >= 80 ? { bg: 'bg-blue-50 border-blue-200',    text: 'text-blue-700',   dot: 'bg-blue-500',   title: 'Good' }      :
    score >= 60 ? { bg: 'bg-orange-50 border-orange-200',text: 'text-orange-700', dot: 'bg-orange-500', title: 'Partial' }   :
                  { bg: 'bg-red-50 border-red-200',      text: 'text-red-700',    dot: 'bg-red-500',    title: 'Low' };

  return (
    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
      OCR extraction confidence:&nbsp;
      <strong>{cfg.title}</strong>
      <span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${cfg.bg} border ${cfg.text}`}>
        {score}/100
      </span>
      {score < 80 && <span className="ml-1 text-gray-500">— review fields before saving</span>}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ClaimExtractionModal({ doc: initialDoc, docLabel: initialDocLabel, jobId, onClose, siblingDocs }: Props) {
  // Segmented switcher: which sibling doc is active. Defaults to the index
  // of the doc the modal was opened with (or 0 if not found in the list).
  const [activeIndex, setActiveIndex] = useState(() => {
    if (!siblingDocs) return 0;
    const idx = siblingDocs.findIndex(s => s.doc.id === initialDoc.id);
    return idx >= 0 ? idx : 0;
  });

  const showSwitcher = !!siblingDocs && siblingDocs.length > 1;
  // The rest of this component reads `doc`/`docLabel` exactly as before —
  // shadowing the props here means every existing effect/handler below
  // (which all key off doc.id, doc.download_url, etc.) automatically reacts
  // to a switcher click without any further changes to their bodies.
  const doc = showSwitcher ? siblingDocs![activeIndex].doc : initialDoc;
  const docLabel = showSwitcher ? siblingDocs![activeIndex].docLabel : initialDocLabel;

  // Segmented Estimate/Invoice switcher tab click handler — switches the
  // modal's active doc in place (design Section 6.5).
  function switchTab(index: number) {
    setActiveIndex(index);
  }

  // Document viewer state
  const [blobUrl, setBlobUrl]       = useState<string | null>(null);
  const [pdfBytes, setPdfBytes]     = useState<Uint8Array | null>(null);
  const [rawBuffer, setRawBuffer]   = useState<ArrayBuffer | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [docErr, setDocErr]         = useState<string | null>(null);
  const [rotation, setRotation]     = useState(0);
  const imageZoom = useImageZoom(doc.id);

  // Form state
  const [fields, setFields]           = useState<ExtractionFields>(EMPTY_FIELDS);
  const [savedFields, setSavedFields] = useState<ExtractionFields | null>(null);

  // UI state
  const [extracting, setExtracting]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saveMsg, setSaveMsg]           = useState<string | null>(null);
  const [extractErr, setExtractErr]     = useState<string | null>(null);
  const [confidenceScore, setConfidenceScore] = useState<number | null>(null);
  const [confidenceLabel, setConfidenceLabel] = useState<string | null>(null);
  const [partNameOptions, setPartNameOptions] = useState<string[]>([]);
  const [partTypeOptions, setPartTypeOptions] = useState<string[]>([]);

  const isInvoice  = isInvoiceType(doc.doc_type);
  const isEstimate = isEstimateType(doc.doc_type);
  // OCR Extraction is Documents-tab-restricted to Repair Invoice, DL
  // Front/Back, RC Front/Back, Policy, and Claims Form — Estimates keep
  // manual line-item entry but lose the Auto-Extract (OCR) button.
  const canExtract = isInvoice;

  const isPdf = doc.content_type === 'application/pdf' ||
    /\.pdf$/i.test(doc.file_name || '');
  const isImage = !isPdf && (
    doc.content_type?.startsWith('image/') ||
    /\.(jpg|jpeg|png|webp|heic|gif)$/i.test(doc.file_name || '')
  );

  // ── Fetch document bytes ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setPdfBytes(null);
    setDocErr(null);
    setLoadingDoc(true);

    fetch(doc.download_url)
      .then(r => { if (!r.ok) throw new Error(`Fetch failed (${r.status})`); return r.arrayBuffer(); })
      .then(buf => {
        if (cancelled) return;
        setRawBuffer(buf.slice(0));
        if (isPdf) {
          setPdfBytes(new Uint8Array(buf));
        } else {
          setBlobUrl(URL.createObjectURL(new Blob([buf], { type: doc.content_type || 'image/jpeg' })));
        }
      })
      .catch(e => { if (!cancelled) setDocErr(e instanceof Error ? e.message : 'Load failed'); })
      .finally(() => { if (!cancelled) setLoadingDoc(false); });

    return () => { cancelled = true; };
  }, [doc.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load previously saved extraction ─────────────────────────────────────
  const loadSaved = useCallback(async () => {
    try {
      const res = await fetchWithAuthRetry(
        `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/documents/${doc.id}/extraction`,
        { headers: authHeaders() },
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.extracted_data) {
        const raw = data.extracted_data;
        // Support both old schema (estimate_no) and new Claude schema (estimate_number)
        const f: ExtractionFields = {
          ...EMPTY_FIELDS,
          ...mapClaudeToFields(raw),
          // also merge any flat keys that match our interface directly
          ...(raw.estimate_no      ? { estimate_no:      raw.estimate_no }      : {}),
          ...(raw.invoice_no       ? { invoice_no:       raw.invoice_no }       : {}),
          ...(raw.vendor_gst       ? { vendor_gst:       raw.vendor_gst }       : {}),
          ...(raw.vehicle_reg_no   ? { vehicle_reg_no:   raw.vehicle_reg_no }   : {}),
          ...(raw.labor_charges    ? { labor_charges:    raw.labor_charges }    : {}),
        };
        if (!Array.isArray(f.line_items)) f.line_items = [];
        setFields(f);
        setSavedFields(f);
        if (data.confidence_score != null)  setConfidenceScore(data.confidence_score);
        if (data.confidence)                setConfidenceLabel(data.confidence);
      }
    } catch { /* ignore — first extraction not yet saved */ }
  }, [jobId, doc.id]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  // ── Earlier invoice slots' saved line items (read-only preview) ──────────
  // Pure review convenience: when viewing Repair Invoice 2 or 3, show the
  // already-saved rows from the earlier slot(s) above this doc's own
  // editable table so the adjuster can eyeball totals across invoices
  // without switching documents. Each invoice still only ever saves its
  // own rows -- nothing here is written back anywhere.
  const earlierInvoiceSiblings = useMemo(() => {
    const mySlot = invoiceSlotNumber(doc.doc_type);
    if (mySlot == null || !siblingDocs) return [];
    return siblingDocs
      .filter(s => {
        const slot = invoiceSlotNumber(s.doc.doc_type);
        return slot != null && slot < mySlot;
      })
      .sort((a, b) => (invoiceSlotNumber(a.doc.doc_type) ?? 0) - (invoiceSlotNumber(b.doc.doc_type) ?? 0));
  }, [siblingDocs, doc.doc_type]);

  const [siblingPreviews, setSiblingPreviews] = useState<{ label: string; items: LineItem[] }[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (earlierInvoiceSiblings.length === 0) { setSiblingPreviews([]); return; }
    (async () => {
      const results = await Promise.all(earlierInvoiceSiblings.map(async sib => {
        try {
          const res = await fetchWithAuthRetry(
            `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/documents/${sib.doc.id}/extraction`,
            { headers: authHeaders() },
          );
          if (!res.ok) return { label: sib.docLabel, items: [] as LineItem[] };
          const data = await res.json();
          const raw = data?.extracted_data;
          const items = raw ? (mapClaudeToFields(raw).line_items ?? []) : [];
          return { label: sib.docLabel, items };
        } catch {
          return { label: sib.docLabel, items: [] as LineItem[] };
        }
      }));
      if (!cancelled) setSiblingPreviews(results);
    })();
    return () => { cancelled = true; };
  }, [jobId, earlierInvoiceSiblings]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Part-name lookup: tenant-agnostic, loaded once (same source as the
  // Assessment tab's part-name dropdown — vehicle_schema.vehicle_part_names) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuthRetry(
          `${API_URL}/api/v1/insurance/vehicle-claims/part-names/lookup`,
          { headers: authHeaders() },
        );
        if (!res.ok) throw new Error(`Load failed (${res.status})`);
        const data = await res.json() as { id: number; name: string; code: string }[];
        if (!cancelled) setPartNameOptions(data.map(p => p.name));
      } catch {
        // Non-fatal — dropdown falls back to free-text entry if the lookup fails.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Part-type lookup: same vehicle_schema.vehicle_part_types table as the
  // Assessment tab's Part category dropdown, loaded once ──────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuthRetry(
          `${API_URL}/api/v1/insurance/vehicle-claims/part-types/lookup`,
          { headers: authHeaders() },
        );
        if (!res.ok) throw new Error(`Load failed (${res.status})`);
        const data = await res.json() as { id: number; name: string; code: string }[];
        if (!cancelled) setPartTypeOptions(data.map(p => p.name));
      } catch {
        // Non-fatal — dropdown falls back to an empty list if the lookup fails.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => { setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── Rotate image bytes for Claude ─────────────────────────────────────────
  async function applyViewerRotation(buf: ArrayBuffer, mime: string, deg: number): Promise<Blob> {
    if (deg === 0 || mime === 'application/pdf') return new Blob([buf], { type: mime });
    return new Promise<Blob>(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(new Blob([buf], { type: mime }));
      img.onload = () => {
        const sw = img.width, sh = img.height;
        const swapped = deg % 180 !== 0;
        const cw = swapped ? sh : sw, ch = swapped ? sw : sh;
        const canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        const ctx = canvas.getContext('2d')!;
        ctx.translate(cw / 2, ch / 2);
        ctx.rotate((deg * Math.PI) / 180);
        ctx.drawImage(img, -sw / 2, -sh / 2);
        URL.revokeObjectURL(url);
        canvas.toBlob(b => resolve(b ?? new Blob([buf], { type: mime })), 'image/jpeg', 0.92);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(new Blob([buf], { type: mime })); };
      img.src = url;
    });
  }

  // ── Claude auto-extract ───────────────────────────────────────────────────
  async function handleAutoExtract() {
    setExtracting(true);
    setExtractErr(null);
    try {
      let buf = rawBuffer;
      if (!buf || buf.byteLength === 0) {
        const r = await fetch(doc.download_url);
        if (!r.ok) throw new Error('Could not fetch document for extraction');
        buf = await r.arrayBuffer();
        setRawBuffer(buf.slice(0));
      }

      const mime = doc.content_type || 'image/jpeg';
      const rotated = await applyViewerRotation(buf, mime, rotation);
      const file = new File([rotated], doc.file_name || 'document', { type: rotated.type });

      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', doc.doc_type);

      const res = await fetchWithAuthRetry(`${AI_SVC_URL}/api/v1/ai/document/extract-fields`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `Extraction failed (${res.status})`);
      }

      const result = await res.json();
      const rawFields: Record<string, unknown> = result.fields || {};

      const mapped = mapClaudeToFields(rawFields);
      setFields(prev => ({ ...prev, ...mapped }));
      setConfidenceScore(result.confidence_score ?? null);
      setConfidenceLabel(result.confidence ?? null);
    } catch (e: unknown) {
      setExtractErr(e instanceof Error ? e.message : 'Auto-extract failed');
    } finally {
      setExtracting(false);
    }
  }

  // ── Save extraction ───────────────────────────────────────────────────────
  // Bill/Invoice line items feed the Assessment tab's Bill Part Name dropdown
  // (see bill_line_items.py) — a row with an amount but no Part Name would
  // be unusable there, so Save is blocked client-side, mirroring
  // save_extraction()'s _validate_required_part_names() 400 on the backend.
  // Every row is checked now, including labour rows -- a "Labour" Part Name
  // entry covers that case instead of being exempted.
  function missingPartNameRows(): number[] {
    if (!isInvoice) return [];
    return fields.line_items.reduce<number[]>((acc, item, i) => {
      const amount = parseFloat(item.amount) || 0;
      if (amount !== 0 && !item.part_name.trim()) acc.push(i);
      return acc;
    }, []);
  }

  // Price (unit_price) feeds the Assessment tab's bill_amount directly (see
  // bill_line_items_sync.py) -- a row with a non-zero Amount but no Price
  // would sync a zero bill_amount there, silently losing the figure, so
  // Save is blocked client-side the same way missingPartNameRows() already
  // is (user request 2026-10-16). Check "Tax Incl.?" to auto-fill Price
  // from Amount, or type it in directly.
  function missingPriceRows(): number[] {
    if (!isInvoice) return [];
    return fields.line_items.reduce<number[]>((acc, item, i) => {
      const amount = parseFloat(item.amount) || 0;
      const price = parseFloat(item.unit_price) || 0;
      if (amount !== 0 && price === 0) acc.push(i);
      return acc;
    }, []);
  }

  async function handleSave() {
    const missing = missingPartNameRows();
    if (missing.length > 0) {
      setSaveMsg(`Select a Part Name for line item${missing.length > 1 ? 's' : ''} ${missing.map(i => i + 1).join(', ')} (amount entered but no part selected)`);
      return;
    }
    const missingPrice = missingPriceRows();
    if (missingPrice.length > 0) {
      setSaveMsg(`Enter a Price for line item${missingPrice.length > 1 ? 's' : ''} ${missingPrice.map(i => i + 1).join(', ')} (amount entered but no price) — check "Tax Incl.?" to split it from Amount automatically, or enter Price directly`);
      return;
    }
    const mismatch = billTotalMismatch(fields);
    if (mismatch) {
      setSaveMsg(mismatch);
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      const payload = {
        ...fields,
        confidence: confidenceLabel,
        confidence_score: confidenceScore,
      };
      const res = await fetchWithAuthRetry(
        `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/documents/${doc.id}/extraction`,
        {
          method: 'PUT',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.detail || `Save failed (${res.status})`);
      }
      setSavedFields({ ...fields });
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (e: unknown) {
      setSaveMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // ── Field helpers ─────────────────────────────────────────────────────────
  function setField(key: keyof Omit<ExtractionFields, 'line_items'>, value: string) {
    setFields(prev => ({ ...prev, [key]: value }));
  }

  function setLineItem(i: number, key: keyof LineItem, value: string) {
    setFields(prev => {
      const items = [...prev.line_items];
      const next = { ...items[i], [key]: value };
      // The Type column IS the Part Type dropdown -- item_type derives from
      // it rather than being set directly.
      if (key === 'part_type') next.item_type = isLabourPartType(value) ? 'labour' : 'part';
      // Qty x Price auto-computes Amount -- editing Amount directly still
      // works (it's not locked/derived-only), it just gets overwritten again
      // the next time Qty or Price changes, same as every other auto-derived
      // field in this codebase (e.g. Assessment tab's assessed_amount).
      // Suppressed while "Tax Included?" is checked: Amount is then the
      // fixed tax-inclusive anchor Price+Tax were split from, and Price
      // itself may well be that split's own output -- letting Qty/Price
      // keep driving Amount here would silently shrink it on every
      // keystroke. Toggle the checkbox off/on to re-split from a new Amount.
      if (key === 'quantity' || key === 'unit_price') {
        const qty = parseFloat(next.quantity);
        const price = parseFloat(next.unit_price);
        if (!isNaN(qty) && !isNaN(price) && !next.tax_included) {
          next.amount = (qty * price).toFixed(2);
        }
      }
      items[i] = next;
      return { ...prev, line_items: items };
    });
  }

  // Checked: splits the row's CURRENT Amount (left untouched) into Price
  // (unit_price) + Tax via splitTaxInclusiveAmount, so Price + Tax == Amount.
  // Unchecked: just clears Tax back to 0 -- Amount and Price are left
  // exactly as they are (Amount was never touched to begin with).
  function toggleTaxIncluded(i: number, checked: boolean) {
    setFields(prev => {
      const items = [...prev.line_items];
      const item = items[i];
      if (checked) {
        const total = parseFloat(item.amount);
        if (isNaN(total)) return prev;
        const { price, tax } = splitTaxInclusiveAmount(total);
        items[i] = { ...item, unit_price: price, tax, tax_included: true };
      } else {
        items[i] = { ...item, tax: '0', tax_included: false };
      }
      return { ...prev, line_items: items };
    });
  }

  function toggleTaxIncludedAll(checked: boolean) {
    setFields(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => {
        if (checked) {
          const total = parseFloat(item.amount);
          if (isNaN(total)) return item;
          const { price, tax } = splitTaxInclusiveAmount(total);
          return { ...item, unit_price: price, tax, tax_included: true };
        }
        return { ...item, tax: '0', tax_included: false };
      }),
    }));
  }

  function toggleTaxApplicable(i: number, checked: boolean) {
    setFields(prev => {
      const items = [...prev.line_items];
      items[i] = { ...items[i], tax_applicable: checked };
      return { ...prev, line_items: items };
    });
  }

  function toggleTaxApplicableAll(checked: boolean) {
    setFields(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => ({ ...item, tax_applicable: checked })),
    }));
  }

  function addLineItem() {
    setFields(prev => ({ ...prev, line_items: [...prev.line_items, { ...EMPTY_LINE }] }));
  }

  function removeLineItem(i: number) {
    setFields(prev => ({ ...prev, line_items: prev.line_items.filter((_, idx) => idx !== i) }));
  }

  const isDirty = JSON.stringify(fields) !== JSON.stringify(savedFields ?? EMPTY_FIELDS);
  const amtWarn = amountWarning(fields);
  const billBreakdown = computeBillBreakdown(fields.line_items);
  const billGrandTotal = computeGrandBillTotal(fields);
  const billMismatch = billTotalMismatch(fields);
  const missingPartNameIdx = new Set(missingPartNameRows());
  const missingPriceIdx = new Set(missingPriceRows());
  const allTaxIncluded = fields.line_items.length > 0 && fields.line_items.every(li => li.tax_included);
  const allTaxApplicable = fields.line_items.length > 0 && fields.line_items.every(li => li.tax_applicable);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/75 p-0 sm:items-center sm:p-3 sm:backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex h-full flex-col bg-white overflow-hidden w-full sm:h-[96vh] sm:rounded-xl sm:shadow-2xl"
        style={{ maxWidth: '96vw' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3 flex-shrink-0 bg-white">
          <div>
            <p className="text-sm font-semibold text-gray-900">{docLabel}</p>
            <p className="text-xs text-gray-400">{doc.file_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {canExtract && (
              <button
                onClick={handleAutoExtract}
                disabled={extracting}
                className="flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-60 transition-colors"
              >
                {extracting
                  ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-white" />
                  : <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                }
                {extracting ? 'Extracting…' : 'Auto-Extract (OCR)'}
              </button>
            )}
            <a
              href={doc.download_url}
              download={doc.file_name || true}
              className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </a>
            <button onClick={onClose} className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Segmented Estimate/Invoice switcher (design Section 6.5) ── */}
        {showSwitcher && (
          <div className="flex items-center gap-1 border-b border-gray-100 bg-gray-50 px-5 py-2 flex-shrink-0">
            {siblingDocs!.map((sibling, i) => (
              <button
                key={sibling.doc.id}
                type="button"
                aria-pressed={i === activeIndex}
                onClick={() => switchTab(i)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  i === activeIndex
                    ? 'bg-white text-primary-700 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:bg-white/60 hover:text-gray-700'
                }`}
              >
                {sibling.docLabel}
              </button>
            ))}
          </div>
        )}

        {/* ── Body: LEFT = viewer, RIGHT = form ── */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* Left — Document viewer */}
          <div className="w-1/2 flex-shrink-0 bg-gray-50 flex flex-col border-r border-gray-100">
            {/* Rotate toolbar */}
            {!loadingDoc && !docErr && (
              <div className="flex items-center gap-2 border-b border-gray-100 bg-white px-3 py-1.5 flex-shrink-0">
                <span className="text-xs text-gray-400 mr-1">Rotate:</span>
                <button
                  onClick={() => setRotation(r => (r - 90 + 360) % 360)}
                  className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Left
                </button>
                <button
                  onClick={() => setRotation(r => (r + 90) % 360)}
                  className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  Right
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
                {rotation !== 0 && (
                  <button onClick={() => setRotation(0)}
                    className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-50">
                    Reset
                  </button>
                )}
                {isImage && blobUrl && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={imageZoom.zoomOut}
                      disabled={imageZoom.zoom <= 0.5}
                      className="rounded-md border border-gray-300 bg-white p-1 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                      title="Zoom out"
                    >
                      −
                    </button>
                    <span className="w-9 text-center text-xs tabular-nums text-gray-400">{Math.round(imageZoom.zoom * 100)}%</span>
                    <button
                      onClick={imageZoom.zoomIn}
                      disabled={imageZoom.zoom >= 5}
                      className="rounded-md border border-gray-300 bg-white p-1 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                      title="Zoom in"
                    >
                      +
                    </button>
                    {imageZoom.zoom !== 1 && (
                      <button onClick={imageZoom.reset}
                        className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-50">
                        Reset
                      </button>
                    )}
                  </div>
                )}
                <span className="ml-auto text-xs text-gray-300">{rotation}°</span>
              </div>
            )}

            <div className="flex-1 overflow-auto relative">
              {loadingDoc && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
                  <p className="text-sm text-gray-400">Loading document…</p>
                </div>
              )}
              {!loadingDoc && docErr && (
                <div className="flex flex-col items-center justify-center h-full gap-3 p-6">
                  <p className="text-sm text-red-500">{docErr}</p>
                  <a href={doc.download_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-primary-600 hover:underline">Open in new tab</a>
                </div>
              )}
              {!loadingDoc && !docErr && blobUrl && isImage && (
                <div
                  className="flex h-full items-start justify-center overflow-hidden p-4"
                  onWheel={imageZoom.onWheel}
                  onMouseDown={imageZoom.onMouseDown}
                  onMouseMove={imageZoom.onMouseMove}
                  onMouseUp={imageZoom.stopDrag}
                  onMouseLeave={imageZoom.stopDrag}
                  style={{ cursor: imageZoom.zoom > 1 ? (imageZoom.dragging ? 'grabbing' : 'grab') : 'default' }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={blobUrl}
                    alt={docLabel}
                    draggable={false}
                    style={{
                      transform: `translate(${imageZoom.pan.x}px, ${imageZoom.pan.y}px) scale(${imageZoom.zoom}) rotate(${rotation}deg)`,
                      transformOrigin: 'center',
                      transition: imageZoom.dragging ? 'none' : 'transform 0.15s ease',
                    }}
                    className="max-w-full object-contain rounded shadow select-none"
                  />
                </div>
              )}
              {!loadingDoc && !docErr && isPdf && pdfBytes && (
                <PdfViewer pdfBytes={pdfBytes} maxWidth={900} />
              )}
            </div>
          </div>

          {/* Right — Extraction form */}
          <div className="w-1/2 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

              {/* Confidence banner */}
              {confidenceScore !== null && confidenceLabel && (
                <ConfidenceBadge score={confidenceScore} label={confidenceLabel} />
              )}

              {/* Amount mismatch warning */}
              {amtWarn && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <svg className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <p className="text-xs text-amber-700">{amtWarn}</p>
                </div>
              )}

              {/* Bill total mismatch -- blocks saving (see handleSave) */}
              {billMismatch && (
                <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2">
                  <svg className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <p className="text-xs font-medium text-red-700">{billMismatch}</p>
                </div>
              )}

              {/* Extraction error */}
              {extractErr && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
                  <svg className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <div>
                    <p className="text-xs font-semibold text-red-700">Extraction failed</p>
                    <p className="text-xs text-red-600 mt-0.5">{extractErr}</p>
                  </div>
                </div>
              )}

              {/* ── Document No. & Date ── */}
              {isInvoice && (
                <Section title="Invoice Details">
                  <TwoCol>
                    <Field label="Invoice No." required>
                      <Inp value={fields.invoice_no} onChange={v => setField('invoice_no', v)} placeholder="INV-2025-001" />
                    </Field>
                    <Field label="Invoice Date" required>
                      <Inp value={fields.invoice_date} onChange={v => setField('invoice_date', v)} placeholder="25.05.2026" />
                    </Field>
                  </TwoCol>
                </Section>
              )}

              {isEstimate && (
                <Section title="Estimate Details">
                  <TwoCol>
                    <Field label="Estimate No." required>
                      <Inp value={fields.estimate_no} onChange={v => setField('estimate_no', v)} placeholder="ET26050041" />
                    </Field>
                    <Field label="Estimate Date" required>
                      <Inp value={fields.estimate_date} onChange={v => setField('estimate_date', v)} placeholder="25.05.2026" />
                    </Field>
                  </TwoCol>
                </Section>
              )}

              {/* ── Garage / Vendor ── */}
              <Section title="Garage / Vendor">
                <Field label="Vendor Name">
                  <Inp value={fields.vendor_name} onChange={v => setField('vendor_name', v)} placeholder="M/S Popular Motor World Pvt. Ltd" />
                </Field>
                <Field label="Vendor Address">
                  <Inp value={fields.vendor_address} onChange={v => setField('vendor_address', v)} placeholder="Street, City, State, PIN" />
                </Field>
                <TwoCol>
                  <Field label="GST Number">
                    <Inp value={fields.vendor_gst} onChange={v => setField('vendor_gst', v)} placeholder="29AXXXX0000X1ZX" />
                  </Field>
                  <Field label="Insurance Co.">
                    <Inp value={fields.insurance_company} onChange={v => setField('insurance_company', v)} placeholder="United India Insurance Co." />
                  </Field>
                </TwoCol>
              </Section>

              {/* ── Vehicle Details ── */}
              <Section title="Vehicle Details">
                <TwoCol>
                  <Field label="Reg. Number" required>
                    <Inp value={fields.vehicle_reg_no} onChange={v => setField('vehicle_reg_no', v)} placeholder="KL34H3006" />
                  </Field>
                  <Field label="Model">
                    <Inp value={fields.vehicle_model} onChange={v => setField('vehicle_model', v)} placeholder="New Creta (FH)" />
                  </Field>
                  <Field label="VIN / Chassis">
                    <Inp value={fields.vin_number} onChange={v => setField('vin_number', v)} placeholder="MALPA813LNM326480" />
                  </Field>
                  <Field label="Engine No.">
                    <Inp value={fields.engine_number} onChange={v => setField('engine_number', v)} placeholder="D4FANM563187" />
                  </Field>
                </TwoCol>
              </Section>

              {/* ── Earlier invoice slots (read-only, for reference) ── */}
              {siblingPreviews.filter(p => p.items.length > 0).map(p => (
                <ReadOnlySiblingLineItems key={p.label} label={p.label} items={p.items} />
              ))}

              {/* ── Line Items ── */}
              <Section
                title="Line Items"
                action={
                  <button
                    onClick={() => addLineItem()}
                    className="flex items-center gap-1 rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                    Add Row
                  </button>
                }
              >
                {fields.line_items.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-1">
                    {canExtract ? 'No line items yet — click Auto-Extract or add manually.' : 'No line items yet — add manually.'}
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded border border-gray-100">
                    <table className="min-w-[1250px] w-full table-fixed text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="py-1.5 px-2 text-left font-medium text-gray-500 w-[130px]">Type</th>
                          <th className="py-1.5 px-2 text-left font-medium text-gray-500 w-[190px]">Description</th>
                          <th className="py-1.5 px-2 text-left font-medium text-gray-500 w-[160px]">Part Name</th>
                          <th className="py-1.5 px-2 text-left font-medium text-gray-500 w-[110px]">Repair/Replace</th>
                          <th className="py-1.5 px-2 text-left font-medium text-gray-500 w-[110px]">Part No.</th>
                          <th className="py-1.5 px-2 text-right font-medium text-gray-500 w-[60px]">Qty</th>
                          <th className="py-1.5 px-2 text-right font-medium text-gray-500 w-[85px]">Price</th>
                          <th className="py-1.5 px-2 text-right font-medium text-gray-500 w-[70px]">Tax</th>
                          <th className="py-1.5 px-2 text-right font-medium text-gray-500 w-[85px]">Amount</th>
                          <th className="py-1.5 px-2 text-center font-medium text-gray-500 w-[90px]">
                            <label className="flex items-center justify-center gap-1" title="Treat each row's Amount as tax-inclusive and split out Tax">
                              <input
                                type="checkbox"
                                aria-label="Tax Included? (apply to all rows)"
                                checked={allTaxIncluded}
                                onChange={e => toggleTaxIncludedAll(e.target.checked)}
                                className="h-3.5 w-3.5"
                              />
                              Tax Incl.?
                            </label>
                          </th>
                          <th className="py-1.5 px-2 text-center font-medium text-gray-500 w-[90px]">
                            <label className="flex items-center justify-center gap-1" title="Enable editing Tax % in the Assessment tab for each row">
                              <input
                                type="checkbox"
                                aria-label="Tax Applicable? (apply to all rows)"
                                checked={allTaxApplicable}
                                onChange={e => toggleTaxApplicableAll(e.target.checked)}
                                className="h-3.5 w-3.5"
                              />
                              Tax Appl.?
                            </label>
                          </th>
                          <th className="py-1.5 px-2 w-6" />
                        </tr>
                      </thead>
                      <tbody>
                        {fields.line_items.map((item, i) => (
                          <tr key={i} className="border-b border-gray-100 group hover:bg-gray-50">
                            <td className="py-1 px-2">
                              <select
                                aria-label="Type"
                                className="w-full rounded border border-gray-200 bg-white px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
                                value={item.part_type}
                                onChange={e => setLineItem(i, 'part_type', e.target.value)}
                              >
                                <option value="">Unspecified</option>
                                {item.part_type && !partTypeOptions.includes(item.part_type) && (
                                  <option value={item.part_type}>{item.part_type}</option>
                                )}
                                {partTypeOptions.map(pt => (
                                  <option key={pt} value={pt}>{pt}</option>
                                ))}
                              </select>
                            </td>
                            <td className="py-1 px-2">
                              <input
                                className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
                                value={item.description}
                                onChange={e => setLineItem(i, 'description', e.target.value)}
                                placeholder="Description"
                              />
                            </td>
                            <td className="py-1 px-2">
                              <div className={missingPartNameIdx.has(i) ? 'rounded ring-1 ring-red-400' : undefined}>
                                <PartNameCombobox
                                  value={item.part_name}
                                  options={partNameOptions}
                                  onChange={v => setLineItem(i, 'part_name', v)}
                                />
                              </div>
                            </td>
                            <td className="py-1 px-2">
                              <select
                                aria-label="Repair/Replace"
                                className="w-full rounded border border-gray-200 bg-white px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
                                value={item.repair_type}
                                onChange={e => setLineItem(i, 'repair_type', e.target.value)}
                              >
                                <option value="">—</option>
                                <option value="Repair">Repair</option>
                                <option value="Replace">Replace</option>
                              </select>
                            </td>
                            <td className="py-1 px-2">
                              <input
                                className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
                                value={item.part_number}
                                onChange={e => setLineItem(i, 'part_number', e.target.value)}
                                placeholder="Part No."
                              />
                            </td>
                            <td className="py-1 px-2">
                              <input
                                className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-primary-400"
                                value={item.quantity}
                                onChange={e => setLineItem(i, 'quantity', e.target.value)}
                                placeholder="1"
                              />
                            </td>
                            <td className="py-1 px-2">
                              <div className={missingPriceIdx.has(i) ? 'rounded ring-1 ring-red-400' : undefined}>
                                <input
                                  className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-primary-400"
                                  value={item.unit_price}
                                  onChange={e => setLineItem(i, 'unit_price', e.target.value)}
                                  placeholder="0.00"
                                />
                              </div>
                            </td>
                            <td className="py-1 px-2">
                              <input
                                className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-primary-400"
                                value={item.tax}
                                onChange={e => setLineItem(i, 'tax', e.target.value)}
                                placeholder="0.00"
                              />
                            </td>
                            <td className="py-1 px-2">
                              <input
                                className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-primary-400"
                                value={item.amount}
                                onChange={e => setLineItem(i, 'amount', e.target.value)}
                                placeholder="0.00"
                              />
                            </td>
                            <td className="py-1 px-2 text-center">
                              <input
                                type="checkbox"
                                aria-label="Tax Included?"
                                checked={!!item.tax_included}
                                onChange={e => toggleTaxIncluded(i, e.target.checked)}
                                className="h-3.5 w-3.5"
                              />
                            </td>
                            <td className="py-1 px-2 text-center">
                              <input
                                type="checkbox"
                                aria-label="Tax Applicable?"
                                checked={item.tax_applicable}
                                onChange={e => toggleTaxApplicable(i, e.target.checked)}
                                className="h-3.5 w-3.5"
                              />
                            </td>
                            <td className="py-1 px-1 text-center">
                              <button
                                onClick={() => removeLineItem(i)}
                                className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* ── Amount Summary ── */}
              <Section title="Amount Summary">
                {/* Parts fields on the left, Labour fields on the right --
                    each its own vertical group, not interleaved row by row
                    (user request 2026-10-16). */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  <div className="space-y-2">
                    <Field label="Parts Cost">
                      <Inp value={fields.parts_cost} onChange={v => setField('parts_cost', v)} placeholder="0.00" align="right" />
                    </Field>
                    {/* Derived from the Line Items table above (Tax Incl.? x
                        Type=Part) -- read-only, not independently editable. */}
                    <Field label="Parts Total (Tax Incl.)">
                      <div className="w-full rounded-md border border-gray-100 bg-gray-50 px-2.5 py-1.5 text-right text-xs text-gray-700">
                        {billBreakdown.partsTaxIncl.toFixed(2)}
                      </div>
                    </Field>
                    <Field label="Parts Total (Tax Not Incl.)">
                      <div className="w-full rounded-md border border-gray-100 bg-gray-50 px-2.5 py-1.5 text-right text-xs text-gray-700">
                        {billBreakdown.partsTaxNotIncl.toFixed(2)}
                      </div>
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field label="Labour Charges">
                      <Inp value={fields.labor_charges} onChange={v => setField('labor_charges', v)} placeholder="0.00" align="right" />
                    </Field>
                    {/* Derived from the Line Items table above (Tax Incl.? x
                        Type=Labour) -- read-only, not independently editable. */}
                    <Field label="Labour Total (Tax Incl.)">
                      <div className="w-full rounded-md border border-gray-100 bg-gray-50 px-2.5 py-1.5 text-right text-xs text-gray-700">
                        {billBreakdown.labourTaxIncl.toFixed(2)}
                      </div>
                    </Field>
                    <Field label="Labour Total (Tax Not Incl.)">
                      <div className="w-full rounded-md border border-gray-100 bg-gray-50 px-2.5 py-1.5 text-right text-xs text-gray-700">
                        {billBreakdown.labourTaxNotIncl.toFixed(2)}
                      </div>
                    </Field>
                  </div>
                </div>

                <TwoCol>
                  <Field label="Tax / GST">
                    <Inp value={fields.tax_amount}     onChange={v => setField('tax_amount', v)}     placeholder="0.00" align="right" />
                  </Field>
                  <Field label="Discount">
                    <Inp value={fields.discount_amount} onChange={v => setField('discount_amount', v)} placeholder="0.00" align="right" />
                  </Field>
                </TwoCol>

                <Field label="Computed Bill Total (sum of the 4 above + Tax/GST − Discount)">
                  <div className={`w-full rounded-md border px-2.5 py-1.5 text-right text-xs font-semibold ${
                    billMismatch ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-100 bg-gray-50 text-gray-700'
                  }`}>
                    {billGrandTotal.toFixed(2)}
                  </div>
                </Field>

                <Field label="Total Amount" required>
                  <Inp value={fields.total_amount} onChange={v => setField('total_amount', v)} placeholder="0.00" align="right" bold />
                </Field>
              </Section>
            </div>

            {/* ── Save footer ── */}
            <div className="flex-shrink-0 border-t border-gray-100 px-5 py-3 bg-white flex items-center justify-between">
              <p className={`text-xs truncate max-w-xs ${saveMsg === 'Saved' ? 'text-green-600' : 'text-red-500'}`}>
                {saveMsg ?? ''}
              </p>
              <button
                onClick={handleSave}
                disabled={saving || !isDirty || !!billMismatch}
                title={billMismatch ?? undefined}
                className="flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving && <div className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-white" />}
                {saving ? 'Saving…' : 'Save Extraction'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Small layout sub-components ───────────────────────────────────────────────

function Section({ title, children, action }: {
  title: string; children: React.ReactNode; action?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        {action}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TwoCol({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-3 gap-y-2">{children}</div>;
}

function Field({ label, required, children }: {
  label: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-xs text-gray-500">
        {label}{required && <span className="ml-0.5 text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

// Read-only preview of an earlier invoice slot's already-saved line items --
// pure review convenience so the adjuster can eyeball an earlier invoice's
// rows/total while filling in a later one, without leaving this modal.
// Never editable and never written back anywhere.
function ReadOnlySiblingLineItems({ label, items }: { label: string; items: LineItem[] }) {
  const total = items.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);
  return (
    <Section title={`${label} (saved — read only)`}>
      <div className="overflow-x-auto rounded border border-gray-200 bg-gray-50">
        <table className="min-w-[980px] w-full table-fixed text-xs border-collapse">
          <thead>
            <tr className="border-b border-gray-200 text-gray-400">
              <th className="py-1.5 px-2 text-left font-medium w-[130px]">Type</th>
              <th className="py-1.5 px-2 text-left font-medium w-[190px]">Description</th>
              <th className="py-1.5 px-2 text-left font-medium w-[160px]">Part Name</th>
              <th className="py-1.5 px-2 text-left font-medium w-[110px]">Repair/Replace</th>
              <th className="py-1.5 px-2 text-left font-medium w-[110px]">Part No.</th>
              <th className="py-1.5 px-2 text-right font-medium w-[60px]">Qty</th>
              <th className="py-1.5 px-2 text-right font-medium w-[85px]">Price</th>
              <th className="py-1.5 px-2 text-right font-medium w-[70px]">Tax</th>
              <th className="py-1.5 px-2 text-right font-medium w-[85px]">Amount</th>
              <th className="py-1.5 px-2 text-center font-medium w-[70px]">Tax Appl.?</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="border-b border-gray-100 text-gray-500">
                <td className="py-1 px-2 truncate">{item.part_type || '—'}</td>
                <td className="py-1 px-2 truncate">{item.description || '—'}</td>
                <td className="py-1 px-2 truncate">{item.part_name || '—'}</td>
                <td className="py-1 px-2 truncate">{item.repair_type || '—'}</td>
                <td className="py-1 px-2 truncate">{item.part_number || '—'}</td>
                <td className="py-1 px-2 text-right">{item.quantity || '—'}</td>
                <td className="py-1 px-2 text-right">{item.unit_price || '—'}</td>
                <td className="py-1 px-2 text-right">{item.tax || '—'}</td>
                <td className="py-1 px-2 text-right">{item.amount || '—'}</td>
                <td className="py-1 px-2 text-center">{item.tax_applicable ? '✓' : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="text-gray-500 font-medium">
              <td colSpan={8} className="py-1.5 px-2 text-right">Total</td>
              <td className="py-1.5 px-2 text-right">{total.toFixed(2)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </Section>
  );
}

function Inp({ value, onChange, placeholder, align = 'left', bold = false }: {
  value: string; onChange: (v: string) => void;
  placeholder?: string; align?: 'left' | 'right'; bold?: boolean;
}) {
  return (
    <input
      className={`w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow ${align === 'right' ? 'text-right' : ''} ${bold ? 'font-semibold' : ''}`}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}
